import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import MaterialDefs from './components/game/MaterialDefs';
import ThemeApplier from './theme/ThemeApplier';
import SchermataDiErrore from './components/shared/SchermataDiErrore';
import { ricaricaApp } from './utils/ricarica-app';
import { isAnalyticsAllowed } from './utils/cookie-consent';
import { loadUmami } from './utils/load-umami';
import { catturaCampagna } from './utils/campagna';
import { inviaCampagnaSePresente } from './utils/analytics';
import { initInstallPrompt } from './utils/install-prompt';
import { markInstalledIfStandalone } from './utils/pwa-installed';
import { accendiFontBandiere } from './utils/flag-font';
import './i18n'; // Initialize i18n before rendering
import './index.css';
// Subito DOPO index.css: dichiara i soli valori del tema scuro, sotto
// `html[data-theme="dark"]`. Separato di proposito — vedi la testata del foglio.
import './theme-dark.css';
// Solo la dichiarazione del font di sole bandiere: nessuno lo nomina finche'
// `accendiFontBandiere()` non mette l'attributo, quindi su mobile non si scarica.
import './flag-font.css';

/*
 * La campagna d'arrivo, PRIMA di ogni altra cosa.
 *
 * L'app riscrive il proprio indirizzo al primo effetto (`replaceState` sulla
 * landing, poi `/` -> `/it/`): dopo il mount le `utm_*` non esistono piu'.
 * Questa riga deve restare qui, a modulo, prima del render — vedi il commento
 * in testa a `utils/campagna.ts`.
 */
catturaCampagna();

/*
 * Lo script di misura, se e solo se il consenso c'e' gia'.
 *
 * Due cancelli, e questa riga li attraversa tutti e due: il DOMINIO (il server
 * di prova serve la stessa build e inquinerebbe le statistiche di produzione) e
 * il CONSENSO (art. 5(3) ePrivacy — vedi `load-umami.ts`). A chi non ha ancora
 * risposto al banner questa chiamata non fa niente: sara' il banner stesso a
 * richiamarla quando l'accettazione arriva.
 *
 * La campagna parte da `quandoPronto`, cioe' quando `window.umami` esiste
 * davvero: e' il ramo di chi aveva gia' accettato in una visita precedente.
 */
loadUmami(undefined, inviaCampagnaSePresente);

/*
 * Il chunk lazy che non c'è più dopo un deploy.
 *
 * Un deploy cambia gli hash dei chunk: una scheda aperta da prima chiede un file
 * che non esiste più al primo `import()` — undici `lazy()` in `App.tsx`, dentro un
 * `<Suspense fallback={null}>` senza rete d'errore. Vite emette `vite:preloadError`
 * proprio per questo, e nessuno lo ascoltava: l'errore risaliva fino
 * all'ErrorBoundary e sostituiva l'APP INTERA.
 *
 * Qui si intercetta PRIMA che risalga. `preventDefault()` impedisce a Vite di
 * rilanciarlo; poi si ricarica — ma solo se la scheda non sta in mezzo a qualcosa
 * che un ricarico butterebbe via. `ricaricaApp()` e non `location.reload()`: con il
 * service worker vecchio ancora al comando, un ricarico nudo richiede le stesse
 * risorse precaricate e rimette in piedi lo stesso bundle mancante.
 *
 * `sessionStorage` come freno: se il ricarico non risolve (una `dist` davvero
 * incompleta), un solo giro e poi si lascia salire l'errore, che è meglio di un
 * anello infinito di ricarichi.
 */
const CHIAVE_RICARICO_CHUNK = 'draughts-ricarico-chunk';
window.addEventListener('vite:preloadError', (evento) => {
  const giaProvato = (() => {
    try { return sessionStorage.getItem(CHIAVE_RICARICO_CHUNK) === '1'; } catch { return false; }
  })();
  if (giaProvato) return; // si lascia salire: l'ErrorBoundary mostra il ricarico a mano
  evento.preventDefault();
  try { sessionStorage.setItem(CHIAVE_RICARICO_CHUNK, '1'); } catch { /* modalità privata */ }
  void ricaricaApp();
});
/* A app viva il freno si toglie, o il secondo deploy della giornata non ricaricherebbe. */
window.addEventListener('load', () => {
  try { sessionStorage.removeItem(CHIAVE_RICARICO_CHUNK); } catch { /* modalità privata */ }
});

// Before render on purpose: Chromium fires `beforeinstallprompt` early and only
// once, so React would routinely miss it (see utils/install-prompt.ts).
initInstallPrompt();

// Girare in standalone e' la prova che l'app e' installata. Annotarla qui, una
// volta sola, e' cio' che permette alla scheda del browser — dove `standalone`
// e' sempre falso — di non riproporre l'installazione a chi l'ha gia' fatta.
markInstalledIfStandalone();

/*
 * Le bandiere, prima del primo disegno.
 *
 * Su Chromium/Windows le emoji bandiera si vedono come due lettere ("IT" al
 * posto di 🇮🇹) in tutta l'app: pastiglie variante, menu lingua, paese dei
 * giocatori. Questa chiamata MISURA se succede e, solo in quel caso, accende il
 * font di sole bandiere — vedi `utils/flag-font.ts` e `flag-font.css`.
 *
 * Qui e non dentro React, per la stessa ragione dello script del tema in
 * index.html: l'attributo deve esserci prima che si disegni qualcosa, o le
 * lettere si vedrebbero lampeggiare. Non tocca nulla dove le bandiere gia'
 * funzionano.
 */
accendiFontBandiere();

// Initialize Sentry only if user has given analytics consent (GDPR)
if (import.meta.env.VITE_SENTRY_DSN && isAnalyticsAllowed()) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    // Stamp every event with the build it came from (same SemVer the update prompt
    // compares against). Without it Sentry cannot tell a fresh regression from an
    // old build still cached on someone's phone, and "resolve in the next release"
    // has no release to key on.
    release: __APP_VERSION__,
    tracesSampleRate: 0.1,
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/*
      Il fallback era un `<p>` con una frase INGLESE cablata, senza
      bottoni, e questo è l'UNICO confine d'errore dell'app — nessun boundary di
      rotta, nessun gestore di `vite:preloadError` in tutto `src/`.

      Il caso che la fa comparire più spesso non è un bug del codice, è un DEPLOY:
      `App.tsx` ha undici `lazy()` dentro un `<Suspense fallback={null}>` senza rete
      d'errore, e un deploy cambia gli hash dei chunk — una scheda aperta da prima
      riceve un 404 sul primo `import()`. Chi ci finiva non aveva niente da premere,
      nemmeno un ricarico, che era esattamente ciò che serviva.
    */}
    <Sentry.ErrorBoundary fallback={({ error }) => <SchermataDiErrore error={error} />}>
      {/* Fratello di <App /> e non figlio: i pattern dei materiali servono a OGNI
          scacchiera, e App è uno state-router a return anticipati dove un ramo può
          dimenticarsi un pezzo di contorno. Qui non può succedere. */}
      <MaterialDefs />
      {/* Stesso motivo del vicino: il tema della cornice vale su OGNI schermata,
          landing e schermata d'errore comprese, e qui non c'e' un ramo che possa
          dimenticarselo. Scrive solo `data-theme` su <html>; il primo disegno lo
          decide lo script in <head>, che gira prima di React. */}
      <ThemeApplier />
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
